/**
 * @fileoverview Canonical i18next factory for every Colony app.
 *
 * Replaces every per-app local `i18n.js` (planner-web, prep-web,
 * pos-web, payday-web, palm-web tenant, palm-web platform-admin,
 * port-web, patrol-web, purchase-web, console, palm-native) with a single
 * factory invocation.
 *
 * Critical design choices:
 *
 *   1. Uses `i18next.createInstance()` — NEVER mutates the default
 *      singleton. Subdomain-coexistent apps (e.g. palm-web tenant +
 *      palm-web/platform-admin) can coexist in one browser process
 *      without colliding.
 *
 *   2. Synchronous boot — `init({ initImmediate: false })` so
 *      `i18n.isInitialized` is true the moment the factory returns.
 *      No async dance, no Suspense fallback flicker. This holds for
 *      `lazyResources` adopters too: the instance is initialized and
 *      renderable synchronously, in the EAGER catalog. What the returned
 *      `resourcesReady` promise gates is only the first render of an app
 *      whose resolved language was deferred — see (6).
 *
 *   3. Initial language is resolved via `getInitialLanguage` (JWT
 *      claim → storage cache → navigator → DEFAULT_LANGUAGE).
 *
 *   4. `changeLanguage` returned from the factory is a closure over
 *      the storage adapter — it both flips i18next AND write-throughs
 *      to storage. Apps NEVER call `i18n.changeLanguage` directly;
 *      they call this returned helper (or the bridge hooks fire it
 *      automatically when DB-backed preferences change).
 *
 *   5. Shell strings (`@puffin-core/page-shell`) are registered under
 *      the canonical `shell` namespace on the fresh instance.
 *
 *   6. `lazyResources` (opt-in) DEFERS the catalogs a session will not
 *      use, so an English speaker stops downloading the Spanish catalog
 *      and vice versa. The resolved language is always fetched eagerly
 *      and `resourcesReady` gates first render; a failed fetch degrades
 *      to the eager fallback catalog, never to raw dotted keys. Full
 *      rationale in `./lazyResources.js`.
 *
 * @module @puffin-core/i18n-bootstrap/createAppI18n
 */

import i18next from 'i18next';
import { initReactI18next, setI18n } from 'react-i18next';

import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LANGUAGE_CODES,
} from './constants.js';
import { getInitialLanguage } from './getInitialLanguage.js';
import {
  APP_TRANSLATION_NAMESPACE,
  createLazyResourceLoader,
  installLazyResourceChangeLanguage,
  normalizeLazyResources,
} from './lazyResources.js';
import { isAdvertisedLanguage } from './localeMetadata.js';
import { registerNamespaces } from './namespaceRegistry.js';
import { pseudoLocalizeBundle } from './pseudoLocale.js';

// I18N-BOOTSTRAP-DEPCYCLE-001 (2026-05-25): `loadShellStrings` is NO LONGER
// statically imported from `@puffin-core/page-shell`. That import was an
// inverted, undeclared dependency (foundational i18n-bootstrap reaching UP
// into the UI-layer page-shell package) AND the root of a module-eval
// circular dependency: page-shell's barrel → `<UserProfilePage>` →
// i18n-bootstrap barrel → createAppI18n → page-shell barrel (partially
// evaluated → `loadShellStrings`/`DASHBOARD_CUSTOMIZER_EN` resolve to
// `undefined`), which crashed every consumer that mounted a page-shell
// surface in a fresh module graph (e.g. pos-web's DashboardPage test tree).
// The canonical shell-string loader is now DEPENDENCY-INJECTED via
// `config.loadShellStrings` (see JSDoc). Each authenticated app's i18n
// bootstrap imports `loadShellStrings` from `@puffin-core/page-shell`
// (the correct UI→app→foundational direction) and passes it in. Marketing
// portals register no shell strings (`shellLocales: []`) and pass nothing.

/**
 * Build a fresh i18next instance pre-bound to the supplied resources +
 * storage adapter, plus the canonical `changeLanguage(code)` helper.
 *
 * @param {Object} config
 * @param {string} config.appName - For diagnostics. Required.
 * @param {boolean} [config.bindReactDefault=true] - Bind react-i18next's process-wide DEFAULT instance
 *   (`initReactI18next` + `setI18n`), as every app does. `false` for a DETACHED React root in the same
 *   page (the dev-only debug overlay, Q-SG-22): the instance is built identically but left un-bound, and
 *   the caller hands it over through `<I18nextProvider>`, so the app's default is never replaced.
 * @param {Object<string, Object>} config.resources - Translation
 *   bundles keyed by BCP-47 short tag (`{ en: {...}, es: {...} }`).
 *   Must contain at least one entry. These ship in the FIRST-PAINT payload.
 * @param {Object<string, () => Promise<Object>>} [config.lazyResources] - DEFERRED
 *   catalogs, keyed the same way, whose values are loaders resolving to the bundle:
 *   `{ es: () => import('…/es/common.json').then((m) => m.default) }`. A locale listed
 *   here is fetched only when a session actually resolves to it, so the other languages
 *   leave the first-paint payload entirely. See `./lazyResources.js` for the full
 *   rationale; the three properties that make it safe are:
 *
 *   · **Opt-in.** Absent at a call site ⇒ every added code path is skipped and the
 *     instance's `changeLanguage` is not even decorated. Behaviour is byte-identical to
 *     before, which is what keeps the ~1,100-suite `pos-web` harness (`resources: { en }`,
 *     `lng: 'en'`, synchronous) untouched.
 *   · **The RESOLVED language is never deferred in effect.** When `getInitialLanguage`
 *     lands on a deferred locale the factory fetches it immediately and the returned
 *     `resourcesReady` promise gates the app's first render — i18next renders a missing
 *     leaf as the raw dotted key, so mounting early would show key paths to a user.
 *   · **A failed fetch degrades to a real language.** Nothing switches, i18next keeps
 *     resolving through `fallbackLng` into the eager catalog, the failure is logged once,
 *     and the attempt is discarded so the next request retries.
 *
 *   🔴 A locale may not appear in BOTH `resources` and `lazyResources`, and a code
 *   outside `SUPPORTED_LANGUAGE_CODES` is rejected — both throw at boot, by name.
 * @param {Object} [config.storage] - Storage adapter
 *   (`createWebStorageAdapter` / `createNativeStorageAdapter` /
 *   `createMemoryAdapter`). Used for sync initial-language read AND
 *   write-through on `changeLanguage`.
 * @param {string|null} [config.jwtToken] - Raw access token; if its
 *   payload carries a `language` claim, that wins over the storage
 *   cache.
 * @param {Object|null} [config.navigator] - DI'd navigator-like
 *   (`{language: 'es-MX'}`). Default: globalThis.navigator. Used as
 *   the lowest-priority signal before DEFAULT_LANGUAGE.
 * @param {ReadonlyArray<Object>} [config.namespaces] - The DECLARATIVE NAMESPACE REGISTRY
 *   (AAS-I18N-002 ruling 39) — an array of `{ namespace, supportedLocales, register }` descriptors
 *   from `@puffin-core/i18n-namespaces`. Authenticated surfaces pass `APP_I18N_NAMESPACES` (shell ·
 *   login · onboarding · captcha); marketing portals pass `MARKETING_I18N_NAMESPACES` (login ·
 *   captcha); a surface that mounts no shared chrome passes `[]` or omits it.
 *
 *   🔴 **This replaces `loadShellStrings` + `shellLocales` + `loadLoginStrings` + `loginLocales`,
 *   which are GONE — Gen-0, no compatibility arm.** Those four made namespace registration
 *   something each app had to remember: `loadShellStrings`/`loadLoginStrings` were hand-wired in 10
 *   apps each, `loadOnboardingStrings` was called separately AFTER this factory returned in 10 more
 *   places with `['en','es']` hardcoded at every call site, and `@puffin-core/captcha-ui` gave up
 *   and registered itself from a React RENDER. Twenty hand-wired loaders IS the rule-of-three
 *   violation the repo's doctrine forbids.
 *
 *   ⚠️ **I18N-BOOTSTRAP-DEPCYCLE-001 is unchanged and still the binding constraint.** This package
 *   must never import `@puffin-core/page-shell` (a module-eval cycle that resolved
 *   `loadShellStrings` to `undefined` and crashed every consumer mounting a page-shell surface), so
 *   registration is still INJECTED. What changed is the arity: injected once, from one composition
 *   module, instead of twenty times by hand.
 *
 *   ⚠️ Each descriptor carries its OWN `supportedLocales`, so this factory no longer holds a copy of
 *   any UI package's locale capability. It used to carry two —
 *   `PAGE_SHELL_SUPPORTED = new Set(['en','es','en-XA'])` and `LOGIN_SHELL_SUPPORTED` — a
 *   foundational package restating a UI package's private fact, with nothing comparing the copies.
 * @param {Object} [config.options] - Extra i18next init options merged
 *   on top of factory defaults (caller options win on conflict).
 * @returns {{
 *   i18n: import('i18next').i18n,
 *   changeLanguage: (code: string) => Promise<void>,
 *   resourcesReady: Promise<void>,
 * }} `resourcesReady` settles once every catalog the RESOLVED language needs is
 *   registered. Already-resolved when nothing was deferred, so awaiting it is free for
 *   the nine apps that have not adopted `lazyResources`. It NEVER rejects — a failed
 *   fetch resolves it after falling back — so a `.catch()` at the call site would be
 *   handling a scenario that cannot happen.
 */
export function createAppI18n(config) {
  if (!config || typeof config !== 'object') {
    throw new Error('createAppI18n: config object is required');
  }
  const {
    appName,
    resources,
    storage,
    jwtToken,
    navigator,
    namespaces,
    options,
    lazyResources,
    bindReactDefault = true,
  } = config;

  if (typeof appName !== 'string' || appName.length === 0) {
    throw new Error('createAppI18n: appName is required (non-empty string)');
  }
  if (!resources || typeof resources !== 'object' || Object.keys(resources).length === 0) {
    throw new Error(
      `createAppI18n[${appName}]: resources is required (at least one locale bundle)`,
    );
  }

  // 🔴 AAS-I18N-002 ruling 39 — REMOVED PARAMETERS ARE A HARD ERROR, NOT A SILENT IGNORE.
  //
  // Gen-0 deleted `loadShellStrings` / `loadLoginStrings` / `shellLocales` / `loginLocales` with no
  // compatibility arm. JavaScript's own default behaviour for a removed option is to ignore it, and
  // that is precisely the wrong failure direction here: a call site still passing `loadShellStrings`
  // would boot, register NOTHING, and render English chrome under a correct `lang="es"` — which is
  // the exact defect this initiative spent weeks chasing (the login screen was English on all ten
  // apps for months while every gate read zero).
  //
  // So a stale key throws at boot, by NAME, with the replacement. Loud beats silent; this is not a
  // shim, it is a tombstone.
  if (typeof bindReactDefault !== 'boolean') {
    throw new Error(`createAppI18n[${appName}]: bindReactDefault must be a boolean when given`);
  }
  const REMOVED_PARAMS = ['loadShellStrings', 'loadLoginStrings', 'shellLocales', 'loginLocales'];
  const stale = REMOVED_PARAMS.filter((key) => key in config);
  if (stale.length > 0) {
    throw new Error(
      `createAppI18n[${appName}]: [${stale.join(', ')}] were REMOVED by AAS-I18N-002 ruling 39. ` +
        'Namespace registration is no longer something a call site wires by hand. Pass ' +
        '`namespaces: APP_I18N_NAMESPACES` from `@puffin-core/i18n-namespaces` (authenticated ' +
        'surfaces), `namespaces: MARKETING_I18N_NAMESPACES` from ' +
        '`@puffin-core/i18n-namespaces/marketing` (public portals), or `namespaces: []` for a ' +
        'surface that mounts no shared chrome.',
    );
  }

  // DEFERRED catalogs (see `./lazyResources.js`). Validated here, before anything reads
  // it, so a malformed option is a named boot error rather than a locale that silently
  // never loads. `null` when the caller opted out — which is every call site but one.
  const lazyLoaders = normalizeLazyResources({ appName, resources, lazyResources });

  /**
   * Every locale this app can produce a catalog for, eager or deferred.
   *
   * 🔴 Both halves are load-bearing and the SECOND is the easy one to miss. Deferring
   * `es` shrinks `resources` to `{ en }`, and two things downstream key off the supplied
   * set: `isPseudoLocaleCode` (which decides whether a code's bundle may be DERIVED over)
   * and `requestedLocales` (which decides the locales offered to every shared namespace —
   * `shell` · `login` · `onboarding` · `captcha`). Reading `resources` alone there would
   * have registered no Spanish chrome at all, so switching to Spanish would have produced
   * a Spanish app inside English page-shell chrome.
   *
   * @type {Set<string>}
   */
  const suppliedLocales = new Set([
    ...Object.keys(resources),
    ...(lazyLoaders ? Object.keys(lazyLoaders) : []),
  ]);

  // Resolve nav at call time (allows DI override for tests; falls
  // back to globalThis.navigator which is always defined under our
  // Node 22+ / modern-browser baseline; nullish coalesces if absent).
  const resolvedNavigator = navigator !== undefined ? navigator : (globalThis.navigator ?? null);

  // BLOG-STD-001 full-shell SSR: when the document was server-prerendered for a
  // specific locale, the `<script type="application/json" id="__blog-ssr-seed__">`
  // data block carries the locale as its authoritative boot value so the hydrated
  // marketing chrome matches the server markup. The element ID is the documented
  // contract emitted by `@puffin-core/marketing-shell`'s `buildHtmlDocument` (we
  // intentionally do NOT depend on marketing-shell here — this 5-line read keeps
  // i18n-bootstrap independent of the chrome package). Present only on
  // prerendered blog documents; absent for every normal app boot. The data block
  // is CSP-safe (`application/json` scripts are not evaluated, so a strict
  // `script-src 'self'` does not gate them).
  let ssrLocale = null;
  if (typeof document !== 'undefined') {
    const seedEl = document.getElementById('__blog-ssr-seed__');
    if (seedEl) {
      try {
        const parsed = JSON.parse(seedEl.textContent || '');
        if (parsed && typeof parsed.locale === 'string') ssrLocale = parsed.locale;
      } catch {
        ssrLocale = null;
      }
    }
  }

  // Compute the initial language synchronously via the priority chain.
  const initialLng = getInitialLanguage({
    ssrLocale,
    jwtToken: jwtToken ?? null,
    storage: storage ?? null,
    navigator: resolvedNavigator,
    supportedCodes: SUPPORTED_LANGUAGE_CODES,
  });

  // Translate the user's `{ en: {...}, es: {...} }` map into i18next's
  // `{ en: { translation: {...} }, es: { translation: {...} } }` shape.
  const i18nextResources = {};
  for (const [code, bundle] of Object.entries(resources)) {
    i18nextResources[code] = { [APP_TRANSLATION_NAMESPACE]: bundle };
  }

  /**
   * Is `code` a locale whose bundles must be DERIVED rather than shipped?
   *
   * True for an unadvertised code the app supplies no resources for — i.e. a pseudo-locale.
   * False for a real advertised language (it brings its own translations) and false for an
   * unadvertised code the caller explicitly supplied resources for (deriving over those would
   * discard hand-authored copy) — **including one supplied via `lazyResources`, which is
   * exactly as "supplied" as an eager one, just not fetched yet**.
   *
   * ⚠️ Stated ONCE and consumed by both `derivePseudoTranslation` and the namespace
   * installer. It was duplicated across the two until W9 (2026-08-10), and duplicating a
   * predicate that decides "may I overwrite a shipping locale?" is how the two halves drift
   * into disagreeing about the same code.
   *
   * @param {string} code - Language code.
   * @returns {boolean} True when bundles for `code` should be derived.
   */
  function isPseudoLocaleCode(code) {
    // 🔴 The string guard is LOAD-BEARING and its absence was a live crash (AAS-I18N-002, 2026-08-10).
    //
    // `i18n.resolvedLanguage` is `undefined` — not `'en'` — whenever no bundle resolves, which is
    // exactly what an instance booted with empty `resources` has (measured: `resolvedLanguage
    // = undefined` while `language = 'en'`). Both remaining clauses then read TRUE for `undefined`:
    // `isAdvertisedLanguage(undefined)` is false, and `resources[undefined]` is absent. So the
    // predicate answered "yes, this is a pseudo-locale" about a language that does not exist, the
    // store's `added` handler called `installPseudoNamespaces(undefined)`, and i18next threw
    // `TypeError: Cannot read properties of undefined (reading 'includes')` from deep inside
    // `hasResourceBundle`.
    //
    // Proved PRE-EXISTING by a revert-run-restore, not assumed: HEAD's factory against HEAD's test
    // file fails **7 of 12** in `onboarding-ui`'s `loadOnboardingStrings` suite. Ruling 39 does not
    // cause it — but it does RAISE THE EXPOSURE, because the four shared namespaces now register at
    // BOOT (inside `createAppI18n`) rather than lazily at first render, so the `added` handler fires
    // during construction for every app.
    //
    // A non-string is not a locale. Saying so here fixes both arms at once — the boot path and the
    // `added` path — because both route through this predicate.
    if (typeof code !== 'string' || code.length === 0) {
      return false;
    }
    return !isAdvertisedLanguage(code) && !suppliedLocales.has(code);
  }

  /**
   * Derive the `translation` bundle for an unadvertised pseudo-locale from the
   * app's own source catalog, and hand it to i18next.
   *
   * 🔴 MEASURED, not assumed: registering only page-shell's `shell`-namespace
   * `en-XA` bundle leaves `i18n.resolvedLanguage === 'en'`, because i18next
   * resolves against the DEFAULT namespace. `useSyncHtmlLang` reads
   * `resolvedLanguage` first, so the page would have been announced `lang="en"`
   * with `dir="ltr"` while nominally in the pseudo-locale — inert, and silently
   * so. See `./pseudoLocale.js`.
   *
   * Derived LAZILY: an app that never boots the pseudo-locale never pays the
   * deep copy of its catalog. That is a branch on LOCALE METADATA, not on
   * `NODE_ENV` — this package contains no environment branch.
   *
   * @param {string} code
   * @returns {Object|null} The i18next-shaped bundle, or null when `code` is a
   *   real advertised language (which brings its own translations).
   */
  function derivePseudoTranslation(code) {
    if (!isPseudoLocaleCode(code)) {
      return null;
    }
    const source = resources[DEFAULT_LANGUAGE] ?? Object.values(resources)[0];
    return { [APP_TRANSLATION_NAMESPACE]: pseudoLocalizeBundle(source) };
  }

  const initialPseudo = derivePseudoTranslation(initialLng);
  if (initialPseudo) {
    i18nextResources[initialLng] = initialPseudo;
  }

  const factoryDefaults = {
    resources: i18nextResources,
    lng: initialLng,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    initImmediate: false,
  };

  const mergedOptions = options ? mergeInitOptions(factoryDefaults, options) : factoryDefaults;

  // Fresh per-app instance — never mutate the default singleton.
  const i18n = i18next.createInstance();
  // `bindReactDefault: false` (SECURITY-HARDENING-GEMS Q-SG-22) — a DETACHED React root in the same page
  // (the dev-only debug overlay) must not replace the app's react-i18next default: every app component
  // reading `useTranslation` without a provider reads that default. Such a root gets this same factory
  // instance, un-bound, and hands it over through an explicit `<I18nextProvider>`.
  if (bindReactDefault) {
    i18n.use(initReactI18next);
  }
  i18n.init(mergedOptions);

  // I18N-LANG-001 hot-fix (2026-05-22): explicitly call `setI18n` from
  // the SAME react-i18next module that consumers import. Vite's
  // dep-optimizer can produce a separate browser-side bundle of
  // react-i18next for `@puffin-core/i18n-bootstrap` versus the apps —
  // when that happens, `initReactI18next.init(this)` updates the
  // factory-side module-state but components reading via `useTranslation`
  // (through their own copy of react-i18next) see `getI18n() === undefined`
  // and fall back to the not-ready `t` that returns the key path as-is.
  // Calling `setI18n` directly from this module guarantees the registry
  // entry is set on the same module-graph node that consumers will read
  // when this package is pre-bundled together with them. The call is
  // idempotent — initReactI18next's internal `setI18n` (if it ran on
  // the same module) just gets the same instance written twice.
  if (bindReactDefault && typeof setI18n === 'function') {
    setI18n(i18n);
  }

  // ── AAS-I18N-002 ruling 39: ONE declarative namespace registry ──────────────────────────────
  //
  // Every shared namespace this instance renders is registered here, from the descriptor list the
  // caller injected. The loaders are still DEPENDENCY-INJECTED — I18N-BOOTSTRAP-DEPCYCLE-001 means
  // this foundational package must never import a UI-layer package — but they arrive as ONE
  // registry from one composition module (`@puffin-core/i18n-namespaces`) rather than as 1–4
  // hand-wired parameters at each of ~30 call sites.
  //
  // 🔴 The locale set offered to each descriptor is `app resources ∪ unadvertised pseudo-locales`,
  // and the second half is load-bearing. Apps pass `resources: { en, es }`, so a naive derivation
  // yields `['en','es']` and page-shell's `en-XA` bundle — a descriptor this factory is handed, then
  // hand-written and since derived from `en` (B45, 2026-09-26) — is never REGISTERED. That
  // is what "ceremonial" meant, and it is why W7 measured only 55 of 177 rendered strings carrying
  // the pseudo-locale marker.
  //
  // ⚠️ The INTERSECTION happens inside `resolveNamespaceLocales`, against each descriptor's own
  // `supportedLocales`. This factory deliberately no longer knows that page-shell serves `en-XA`
  // and login-shell does not; that fact belongs to those packages.
  const pseudoLocaleCodes = SUPPORTED_LANGUAGES.filter((entry) => !entry.advertised).map(
    (entry) => entry.code,
  );
  const requestedLocales = [...new Set([...suppliedLocales, ...pseudoLocaleCodes])];
  registerNamespaces(i18n, namespaces ?? [], requestedLocales);

  /**
   * Derive the pseudo-locale bundle for **every namespace on the instance**, not just
   * `translation`.
   *
   * 🔴 AAS-I18N-002 W9, ruling 28. `derivePseudoTranslation` above covers the app's own
   * `translation` namespace only. Every other namespace a loader registers —
   * `login` (`@puffin-core/login-shell`), `onboarding` (`@puffin-core/onboarding-ui`),
   * `captcha` (`@puffin-core/captcha-ui`) — ships `en` + `es` and nothing else, so under
   * `en-XA` those namespaces fell back to plain English and rendered **unmarked**. That
   * silently inverts the pseudo-locale's whole promise: the docblock says "any string
   * rendering WITHOUT a marker is hardcoded copy the catalog never saw", and an unmarked
   * *catalog-backed* string makes that claim false. Measured by W7 before this existed:
   * **55 of 177** rendered strings carried the marker (**31%**), so "unmarked ⇒ hardcoded"
   * was ~97% false positives.
   *
   * Derived MECHANICALLY from each namespace's own source bundle, never hand-marked — a
   * hand-marked bundle rots the moment a leaf changes in its source. page-shell's hand-written
   * `en-XA.js` proved it: one of its 614 leaves sat seven weeks stale behind a key-shape parity
   * test that cannot see values, and it is now derived by `pseudoLocalizeBundle` too (B45).
   *
   * ⚠️ `overwrite: false` on `addResourceBundle`, so a package that ships a REAL
   * pseudo-locale bundle (page-shell does) keeps it. This only fills genuine holes.
   *
   * @param {string} code - The pseudo-locale code being installed.
   * @returns {void}
   */
  function installPseudoNamespaces(code) {
    // `i18n.store` is i18next's resource store, always present after `init()` above. An app that
    // ships no `en` (the Spanish-only case) legitimately has no source namespaces to derive from.
    const sourceNamespaces = i18n.store.data[DEFAULT_LANGUAGE];
    if (!sourceNamespaces) return;

    for (const namespace of Object.keys(sourceNamespaces)) {
      if (i18n.hasResourceBundle(code, namespace)) continue;
      const derived = pseudoLocalizeBundle(sourceNamespaces[namespace]);
      i18n.addResourceBundle(code, namespace, derived, true, false);
    }
  }

  if (initialPseudo) {
    installPseudoNamespaces(initialLng);
  }

  // A namespace can be registered LONG after boot — `@puffin-core/onboarding-ui` and
  // `@puffin-core/captcha-ui` are loaded by the surfaces that mount them, not by this factory.
  // Deriving only at boot and on `changeLanguage` would therefore leave exactly the
  // lazily-mounted chrome unmarked, which is indistinguishable from that chrome being
  // hardcoded — the false signal this whole change exists to remove. The store's `added` event
  // closes it. Recursion is impossible: the handler only reacts to the SOURCE language and only
  // ever writes a DIFFERENT one.
  i18n.store.on('added', (lng) => {
    if (lng !== DEFAULT_LANGUAGE) return;
    if (!isPseudoLocaleCode(i18n.resolvedLanguage)) return;
    installPseudoNamespaces(i18n.resolvedLanguage);
  });

  // ── DEFERRED catalogs (`./lazyResources.js`) ─────────────────────────────────────────
  //
  // The loader is always constructed — with `loaders: null` it is an inert pass-through
  // whose `ensure()` is an already-resolved `true`, so the code below reads the same for
  // adopters and non-adopters and there is no second boot path to keep in step.
  //
  // The DECORATOR, by contrast, is installed only for adopters. That is deliberate: an
  // app that passes no `lazyResources` keeps i18next's own untouched `changeLanguage`, so
  // "opt-in means byte-identical" is a structural fact rather than a claim about a
  // branch's behaviour.
  const lazyLoader = createLazyResourceLoader({ appName, i18n, loaders: lazyLoaders });
  if (lazyLoaders) {
    installLazyResourceChangeLanguage(i18n, lazyLoader);
  }

  /**
   * Switch language + write-through to the storage cache.
   * Idempotent on the i18next side (calling with the current language
   * is a no-op). Unsupported codes are rejected silently (the dropdown
   * should never offer them, but defense in depth).
   *
   * @param {string} code
   * @returns {Promise<void>}
   */
  async function changeLanguage(code) {
    if (typeof code !== 'string' || !SUPPORTED_LANGUAGE_CODES.includes(code)) {
      return;
    }
    // Fetch a DEFERRED catalog before anything else. Resolves `true` immediately for a
    // code that was never deferred, so this is a no-op for the nine non-adopting apps.
    //
    // 🔴 The early return on failure is what keeps the storage write-through below
    // honest. The language did NOT change — i18next is still rendering the previous one —
    // so caching the requested code would make the next cold boot start in a language the
    // browser has already proved it cannot fetch, and `useSyncHtmlLang` would announce a
    // `<html lang>` the page does not actually speak.
    if (!(await lazyLoader.ensure(code))) {
      return;
    }
    // Switching INTO a pseudo-locale at runtime needs the same derived bundle
    // the boot path installs — without it i18next resolves straight back to the
    // fallback and the switch looks like a no-op.
    if (!i18n.hasResourceBundle(code, APP_TRANSLATION_NAMESPACE)) {
      const pseudo = derivePseudoTranslation(code);
      if (pseudo) {
        i18n.addResourceBundle(
          code,
          APP_TRANSLATION_NAMESPACE,
          pseudo[APP_TRANSLATION_NAMESPACE],
          true,
          true,
        );
      }
    }
    // The sibling namespaces (`login`, `onboarding`, `captcha`, …) need the same treatment on
    // the RUNTIME switch, not only at boot — otherwise switching into the pseudo-locale from a
    // page that already mounted them leaves exactly the shared-package chrome unmarked, which
    // is the specific hole W7 measured.
    //
    // ⚠️ The `isPseudoLocaleCode` guard is LOAD-BEARING and was missing in the first draft. The
    // `hasResourceBundle` check above is per-NAMESPACE, so without this an advertised language
    // that ships no bundle (`fr`) had a marked `translation` bundle INVENTED for it — the app
    // would have rendered `[RTL] Hello` in French instead of falling back to English. Caught by
    // the existing "changeLanguage to a REAL language never derives a pseudo bundle" test.
    if (isPseudoLocaleCode(code)) {
      installPseudoNamespaces(code);
    }
    if (i18n.resolvedLanguage !== code) {
      await i18n.changeLanguage(code);
    }
    // No catch (SWALLOWED-CATCH U3): every adapter this package ships absorbs the platform's own
    // refusal — the web adapter's StorageService writes through `_safeSetItem`, the native adapter
    // persists fire-and-forget with `onWriteError`, the memory adapter cannot refuse. A throw that
    // reaches here is a defect in an adapter, and it surfaces.
    if (storage && typeof storage.set === 'function') {
      storage.set(STORAGE_KEY, code);
    }
  }

  /**
   * Fetch the resolved language's DEFERRED catalog and put it in force.
   *
   * 🔴 The trailing `changeLanguage` is not redundant, and this was MEASURED rather than
   * reasoned (real i18next, 2026-08-14). Booting `lng: 'es'` against `resources: { en }`
   * leaves `language === 'es'` but `resolvedLanguage === 'en'`, and registering the `es`
   * bundle afterwards is enough for `t()` to return Spanish but does **not** move
   * `resolvedLanguage` — it stays `'en'`. `useSyncHtmlLang` reads `resolvedLanguage`
   * first, so without this call the page would render Spanish under `<html lang="en">`:
   * a screen reader would narrate Spanish copy in an English voice, which is the WCAG
   * 3.1.1 defect AAS-I18N-002 closed and would have been silently reopened here.
   *
   * The call goes through the DECORATED `changeLanguage`, whose `ensure()` is a memoized
   * no-op by this point — one seam, called twice, rather than a second load path.
   *
   * @returns {Promise<void>} Never rejects; a failed fetch leaves the eager fallback in
   *   force and is reported by the loader.
   */
  async function loadDeferredResolvedLanguage() {
    if (!lazyLoader.has(initialLng)) {
      return;
    }
    if (!(await lazyLoader.ensure(initialLng))) {
      return;
    }
    await i18n.changeLanguage(initialLng);
  }

  const resourcesReady = loadDeferredResolvedLanguage();

  return { i18n, changeLanguage, resourcesReady };
}

/**
 * Shallow merge of two i18next option bags where the second wins on
 * conflicts. The nested `interpolation` and `react` blocks are merged
 * one level deep so callers can override individual keys without
 * losing factory defaults for siblings.
 *
 * @param {Object} base
 * @param {Object} override
 * @returns {Object}
 */
function mergeInitOptions(base, override) {
  const result = { ...base, ...override };
  if (base.interpolation && override.interpolation) {
    result.interpolation = { ...base.interpolation, ...override.interpolation };
  }
  if (base.react && override.react) {
    result.react = { ...base.react, ...override.react };
  }
  return result;
}
